import type { IConfig } from '../domain/types'
import type { ActivePromotion, IProductCatalog } from '../domain/types/graphql'
import { setPromotionUiDetailsCatalog } from '../domain/promotions/setPromotionUiDetailsCatalog'
import type { CatalogItemInput } from './catalogCard'

/**
 * One entry of the `catalog` field a catalog query returns: a category and the
 * products priced for this session.
 *
 * Declared here rather than reusing `ICatalogItem` from `domain/types/catalogQuery`
 * because that type is one of the two `ICatalogItem`s in the port, and this is
 * the only shape the mapper reads.
 */
export interface CatalogCategoryInput {
  category?: string | null
  productsPrices?: IProductCatalog[] | null
}

/** `sku -> units currently in the cart`. */
export type CartQuantitiesBySku = Readonly<Record<string, number>>

export interface ToCatalogItemInputsArgs {
  categories: readonly CatalogCategoryInput[] | null | undefined
  quantitiesBySku?: CartQuantitiesBySku | undefined
  /**
   * The promotion engine's read view of the storefront config. Without it
   * `setPromotionUiDetailsCatalog` returns undefined for every product and no
   * card gets a badge — which is exactly what happens when the host forgets to
   * dispatch `setConfig`.
   */
  config?: IConfig | undefined
  /** Cart-level active promotions, used to mark a promotion as already applied. */
  activePromotions?: ActivePromotion[] | undefined
}

/**
 * How many units are in the cart, which is what a cart badge counts.
 *
 * Units, not lines: three of one product is three. The same one-line `reduce`
 * had appeared on the catalog, the product detail and the cart page before this
 * existed, and a React Native shell needs the same badge.
 */
export const toCartUnitCount = (
  items: readonly { quantity?: number | null }[] | null | undefined,
): number => (items ?? []).reduce((total, item) => total + (item?.quantity ?? 0), 0)

/**
 * A cart line's sku is nullable and the same sku can appear on more than one
 * line (different packages), so quantities are summed rather than assigned.
 */
export const toQuantitiesBySku = (
  items: readonly { sku?: string | null; quantity?: number | null }[] | null | undefined,
): Record<string, number> => {
  const result: Record<string, number> = {}
  for (const item of items ?? []) {
    const sku = item?.sku
    if (!sku) continue
    result[sku] = (result[sku] ?? 0) + (item.quantity ?? 0)
  }
  return result
}

export interface CategoryFilter {
  name: string
  /** The skus in this category, in the order the query returned them. */
  skus: string[]
}

/**
 * The categories, kept as a separate view of the same result.
 *
 * `toCatalogItemInputs` flattens categories away on purpose — a card does not
 * care which category it came from — which leaves a page with no way to ask a
 * `CatalogItemInput` what it belongs to. Rather than un-flatten that mapper and
 * make every card carry a field only the filter bar reads, this returns the
 * index: category name to the skus in it.
 *
 * The same filters and exclusions as `toCatalogItemInputs` apply, so a category
 * cannot offer a sku the grid will not draw. A category whose products are all
 * inactive disappears entirely, which is correct: an empty filter is a dead
 * control.
 */
export const toCategoryFilters = (
  categories: readonly CatalogCategoryInput[] | null | undefined,
): CategoryFilter[] => {
  const byName = new Map<string, string[]>()

  for (const category of categories ?? []) {
    const name = category?.category
    if (!name) continue

    for (const product of category.productsPrices ?? []) {
      if (!product?.sku || product.isActive === false) continue
      const skus = byName.get(name) ?? []
      // A product listed twice under one category is still one entry.
      if (!skus.includes(product.sku)) skus.push(product.sku)
      byName.set(name, skus)
    }
  }

  return [...byName.entries()].map(([name, skus]) => ({ name, skus }))
}

/**
 * How many products a page of the grid asks for.
 *
 * cinnamon's `PRODUCTS_PAGE_SIZE` is 10, sized for a one-column list. This grid
 * is two columns, so 10 is five rows — barely more than one screen, and a
 * shopper flicking through a catalog would spend the session waiting on pages.
 * 20 is two screens' worth, which is what keeps the next page arriving before
 * the sentinel is reached.
 */
export const CATALOG_PAGE_SIZE = 20

/**
 * Whether the page just received looks like there is another one behind it.
 *
 * The `catalog` field answers with categories, not with a `hasNextPage` — the
 * paginated `search` field is the one that reports it — so this is cinnamon's
 * rule from `setCatalogPagination`, applied across the blocks instead of per
 * category: a block that came back exactly full is a block the server truncated,
 * and a full block anywhere means the next page has something in it.
 *
 * cinnamon also treats an *empty* category as "has more", which is how its own
 * per-category paging survives a category whose first page is empty. Here an
 * empty page ends the scroll: the sentinel would otherwise ask for page after
 * page of nothing, and every one of them would be a round trip.
 */
export const hasMoreCatalogPages = (
  blocks: readonly CatalogCategoryInput[] | null | undefined,
  pageSize: number,
): boolean =>
  (blocks ?? []).some((block) => {
    const count = block?.productsPrices?.length ?? 0
    return count > 0 && count % pageSize === 0
  })

/**
 * One authored category, as the `categories` query returns it. Structural on
 * purpose: `view-models` cannot import `graphql/types` — that module already
 * imports this one for the order shapes, and the cycle would be real.
 */
export interface CategoryNodeInput {
  name?: string | null
  isActive?: boolean | null
  priority?: number | null
  parentId?: string | null
}

/**
 * The chip rail's category names, from the storefront's own category list.
 *
 * Three rules, each of which costs a shopper something when it is missing:
 * inactive categories are gone (a chip that filters to nothing), children are
 * gone (`filterCategoriesForMenu`'s rule — a rail mixing "Bebidas" with
 * "Bebidas / Gaseosas" reads as two unrelated filters), and the order is the
 * authored `priority` rather than insertion order, because that is the only
 * thing the storefront can control.
 *
 * A category with no priority sorts after every category that has one, keeping
 * a half-configured storefront's authored order intact instead of interleaving
 * it with the unconfigured rest.
 */
export const toCategoryOptions = (
  categories: readonly CategoryNodeInput[] | null | undefined,
): string[] => {
  const named = (categories ?? [])
    .filter((category) => category?.isActive !== false && !category?.parentId)
    .map((category) => category?.name)
    .filter((name): name is string => typeof name === 'string' && name.trim() !== '')

  const ordered = (categories ?? []).reduce<Map<string, number>>((priorities, category) => {
    const name = category?.name
    if (typeof name === 'string' && !priorities.has(name)) {
      priorities.set(name, category?.priority ?? Number.MAX_SAFE_INTEGER)
    }
    return priorities
  }, new Map())

  return [...new Set(named)].sort(
    (left, right) =>
      (ordered.get(left) ?? Number.MAX_SAFE_INTEGER) -
      (ordered.get(right) ?? Number.MAX_SAFE_INTEGER),
  )
}

/**
 * `stock` and `outOfStock` are independent signals and either can be null.
 * `outOfStock` is authoritative when true; when the backend reports no stock
 * number at all the product is still sellable, so it maps to 1 — the smallest
 * value `toCatalogCardModel`'s `stock <= 0` reads as in stock.
 */
const availableStock = (product: IProductCatalog): number =>
  product.outOfStock ? 0 : (product.stock ?? 1)

/**
 * The catalog query result as the cards need it: categories flattened, hidden
 * products dropped, cart quantities joined, promotion badges resolved.
 *
 * Deduplicating by sku is not cosmetic. A product that belongs to several
 * categories comes back once per category, and rendering it twice would give
 * React two children with the same key and show the shopper the same product
 * twice.
 */
export const toCatalogItemInputs = ({
  categories,
  quantitiesBySku,
  config,
  activePromotions,
}: ToCatalogItemInputsArgs): CatalogItemInput[] => {
  const seen = new Set<string>()
  const items: CatalogItemInput[] = []

  for (const category of categories ?? []) {
    for (const product of category?.productsPrices ?? []) {
      if (!product?.sku || product.isActive === false || seen.has(product.sku)) {
        continue
      }
      seen.add(product.sku)

      items.push({
        sku: product.sku,
        name: product.name ?? '',
        // `imageURL` is a list; a card shows one image and the first is the
        // primary one. An empty list leaves the placeholder to the view-model.
        image: product.imageURL?.[0] ?? '',
        price: product.price ?? 0,
        stock: availableStock(product),
        quantityInCart: quantitiesBySku?.[product.sku] ?? 0,
        promotionUiDetails: setPromotionUiDetailsCatalog({
          product,
          config,
          activePromotions,
        }),
      })
    }
  }

  return items
}
